import { Component, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { filter, map } from 'rxjs';

import { EntityIconService } from '@gfe/core/services/entity-icon/entity-icon.service';
import { ICONS } from '@gfe/core/services/icons/icons.service';
import { refreshDataspace } from '@gfe/core/store/dataspace/dataspace.actions';
import { selectAvailableEntities } from '@gfe/core/store/dataspace/dataspace.selectors';
import { selectPlan } from '@gfe/core/store/plan/plan.selectors';
import { UserPreferencesActions } from '@gfe/core/store/user-preferences/user-preferences.actions';
import {
  selectEntitiesOrder,
  selectPinnedEntities
} from '@gfe/core/store/user-preferences/user-preferences.selectors';
import { capitalizeFirst } from '@gfe/core/utils/case-conversions';
import { isNil } from '@gfe/core/utils/shared-utils';
import {
  CardConfig,
  CardsListComponent,
  CardsListComponentConfig
} from '@gfe/shared/components/cards-list/cards-list.component';
import {
  SearchConfig,
  ToolboxComponent,
  ToolboxSection
} from '@gfe/shared/components/toolbox/toolbox.component';
import { AvailableLanguages } from '@gfe/shared/types/translation.type';

import { EntityMetadata } from '@core/models/entity/entity-metadata.model';
import { foldForSearch } from '@core/utils/search-text.utils';

import { PlanEditBarComponent } from '@features/components/plan/edit/plan-edit-bar/plan-edit-bar.component';

import { sortEntityCards } from './entities-sort.util';

@Component({
  selector: 'app-entities-list',
  imports: [TranslatePipe, ToolboxComponent, CardsListComponent, PlanEditBarComponent],
  templateUrl: './entities-list.component.html'
})
export class EntitiesListComponent {
  cardListConfig = computed<CardsListComponentConfig>(() => {
    this.currentLang();
    return {
      noDataMessage: this.translate.instant('features.entities.no_data'),
      pinTooltip: this.translate.instant('common.pin'),
      unpinTooltip: this.translate.instant('common.unpin'),
      onPinToggle: (card) =>
        this.store.dispatch(
          UserPreferencesActions.togglePinnedEntity({ entityName: card.id })
        ),
      onReorder: (order) =>
        this.store.dispatch(UserPreferencesActions.setEntitiesOrder({ order })),
      pinnedLabel: this.translate.instant('features.entities.pinned.title'),
      pinnedOrder: this.pinnedEntities(),
      onPinnedReorder: (order) =>
        this.store.dispatch(UserPreferencesActions.reorderPinnedEntities({ order })),
      cards: this.cards()
    };
  });
  protected readonly ICONS = ICONS;
  protected readonly entityIcons = inject(EntityIconService);
  private readonly store = inject(Store);
  private translate = inject(TranslateService);
  protected search = signal<string>('');
  protected currentLang = toSignal(
    this.translate.onLangChange.pipe(
      filter((value) => !isNil(value)),
      map((value) => value.lang)
    ),
    { initialValue: this.translate.getCurrentLang() }
  );
  private readonly plan = this.store.selectSignal(selectPlan);
  /** Set on a plan's data list, whose plan the store holds; null on the data space's own. */
  protected readonly planId = computed(() => this.plan()?.id ?? null);
  protected availableEntities =
    this.store.selectSignal<EntityMetadata[]>(selectAvailableEntities);
  protected pinnedEntities = this.store.selectSignal<string[]>(selectPinnedEntities);
  protected entitiesOrder = this.store.selectSignal<string[]>(selectEntitiesOrder);

  protected searchPlaceholder = computed(() => {
    this.currentLang(); // Trigger reactivity when language changes
    return this.translate.instant('common.search_placeholder');
  });

  constructor() {
    this.store.dispatch(refreshDataspace());
  }

  protected cards = computed<CardConfig[]>(() => {
    const search = foldForSearch(this.search());
    const currentLang = this.currentLang() as AvailableLanguages;
    const entities = this.availableEntities();
    const pinned = new Set(this.pinnedEntities());
    // Locale-aware grouping (e.g. "4 218" in fr, "4,218" in en) for label values.
    const numberFormat = new Intl.NumberFormat(currentLang);

    const matching = entities
      .map((entity) => ({
        id: entity.name,
        title: entity.displayName?.[currentLang] || entity.name || 'Unknown',
        description: entity.description?.[currentLang] || '',
        tooltip: entity.description?.[currentLang] || '',
        iconClass: this.entityIcons.getEntityIcon(entity.name),
        isPinned: pinned.has(entity.name),
        route: entity.name,
        labels: this.buildLabels(entity, numberFormat),
        hasAccent: undefined
      }))
      .filter((entity) => {
        const normalizedTitle = foldForSearch(entity.title);
        const normalizedDescr = foldForSearch(entity.description);
        return normalizedTitle.includes(search) || normalizedDescr.includes(search);
      });

    // The user's manual order (persisted in preferences) applies, with unranked
    // entities falling back to alphabetical order.
    return sortEntityCards(matching, this.entitiesOrder(), currentLang);
  });

  toolboxSections = computed<ToolboxSection[]>(() => {
    this.currentLang();

    return [
      // {
      //   id: 'default actions',
      //   items: [
      //     {
      //       id: 'filter',
      //       icon: ICONS.funnel,
      //       tooltip: this.translate.instant('common.filters'),
      //       callback: () => {}
      //     }
      //   ]
      // }
    ];
  });

  searchCfg: SearchConfig = {
    active: true,
    type: 'simple',
    shortcut: true
  };

  /**
   * Builds the card labels for an entity: the total count plus every `extras`
   * metric. Each key is translated and used as the visible label text, and each
   * value is grouped for the current locale (the cards-list template renders
   * labels as value over key).
   */
  private buildLabels(
    entity: EntityMetadata,
    numberFormat: Intl.NumberFormat
  ): Record<string, string> {
    const entries: [string, number][] = [
      ['total', entity.totalCount],
      ...Object.entries(entity.extras ?? {})
    ];

    return Object.fromEntries(
      entries.map(([key, value]) => [
        this.translateLabelKey(key),
        numberFormat.format(value)
      ])
    );
  }

  /**
   * Resolves a label key against `features.entities.labels.<key>`. When no
   * translation exists, ngx-translate returns the full key path, in which case we
   * fall back to a humanized version of the raw key (e.g. `fleet_size` -> `Fleet
   * size`).
   */
  private translateLabelKey(key: string): string {
    const translationKey = `features.entities.labels.${key}`;
    const translated = this.translate.instant(translationKey);
    if (translated === translationKey) {
      return capitalizeFirst(key.replace(/_/g, ' '));
    }
    return translated;
  }
}
